import styled from '@emotion/styled';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { SingSetup, SongPreview } from '~/interfaces';
import useViewportSize from '~/modules/hooks/useViewportSize';
import { isEurovisionSong } from '~/modules/Songs/utils/specialSongsThemeChecks';
import { FeatureFlags } from '~/modules/utils/featureFlags';
import SongSettings from '~/routes/SingASong/SongSelection/Components/SongSettings';
import { useSpecialTheme } from '~/routes/SingASong/SongSelection/Hooks/useSpecialTheme';

interface Props {
  songPreview: SongPreview;
  onPlay: (setup: SingSetup & { song: SongPreview }) => void;
  keyboardControl: boolean;
  onExitKeyboardControl: () => void;
  top: number;
  left: number;
  width: number;
  height: number;
  isPopular: boolean;
  forceFlag: boolean;
  locked?: boolean;
  onAuthRequired?: () => void;
}

export default function SongPreviewComponent({
  songPreview,
  keyboardControl,
  onExitKeyboardControl,
  onPlay,
  locked = false,
  onAuthRequired,
}: Props) {
  const { width: windowWidth } = useViewportSize();
  const isMobile = (windowWidth ?? 9999) < 640;

  useSpecialTheme(songPreview, FeatureFlags.Eurovision, isEurovisionSong, 'eurovision');

  if (!keyboardControl) {
    return null;
  }

  const modalContext = isMobile ? 'mobilePreview' : 'desktopPreview';

  return (
    <>
      <Backdrop data-mobile={isMobile} onClick={onExitKeyboardControl} />
      <SettingsModal
        data-mobile={isMobile}
        data-test={isMobile ? 'mobile-settings-modal' : 'desktop-settings-modal'}
        onClick={(event) => event.stopPropagation()}>
        <CloseSetupButton type="button" onClick={onExitKeyboardControl} aria-label="Close song setup">
          <CloseRounded aria-hidden="true" />
        </CloseSetupButton>

        {locked ? (
          <LockedPreviewPanel onAuthRequired={onAuthRequired} />
        ) : (
          <SongSettings
            songPreview={songPreview}
            onPlay={onPlay}
            keyboardControl={keyboardControl}
            onExitKeyboardControl={onExitKeyboardControl}
            context={modalContext}
          />
        )}
      </SettingsModal>
    </>
  );
}

function LockedPreviewPanel({ onAuthRequired }: { onAuthRequired?: () => void }) {
  return (
    <LockedPreview>
      <h2>Register for free</h2>
      <p>Create a FREE fastKaraoke account to play every song in the catalog.</p>
      <button type="button" onClick={onAuthRequired}>
        Login
      </button>
    </LockedPreview>
  );
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 880;
  width: 100vw;
  height: 100vh;
  background: rgba(20, 20, 20, 0.56);
  cursor: pointer;

  &[data-mobile='true'] {
    top: var(--karaoke-top-bar-height, 0px);
    height: calc(100vh - var(--karaoke-top-bar-height, 0px));
    background: rgba(20, 20, 20, 0.42);
  }
`;

const SettingsModal = styled.div`
  --preview-padding: 5rem;
  position: fixed;
  z-index: 900;
  left: 50%;
  top: 50%;
  width: calc(100vw - (var(--preview-padding) * 2));
  max-width: 1100px;
  max-height: calc(100dvh - (var(--preview-padding) * 2));
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
  padding: clamp(18px, 2.5vw, 30px);
  border: 1px solid var(--walky-border);
  border-top: 4px solid var(--walky-orange);
  border-radius: 8px;
  background: var(--walky-surface-translucent);
  color: var(--walky-ink);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(18px);
  transform: translate(-50%, -50%);

  &[data-mobile='true'] {
    top: calc(var(--karaoke-top-bar-height, 0px) + 8px);
    right: 8px;
    bottom: auto;
    left: 8px;
    width: auto;
    max-width: none;
    max-height: calc(100dvh - var(--karaoke-top-bar-height, 0px) - env(safe-area-inset-bottom) - 16px);
    padding: 14px 10px calc(env(safe-area-inset-bottom) + 12px);
    transform: none;
  }

  @media (max-width: 900px) {
    --preview-padding: 2rem;
  }
`;

const CloseSetupButton = styled.button`
  position: sticky;
  top: 0;
  margin-left: auto;
  margin-bottom: 8px;
  width: 38px;
  height: 38px;
  min-width: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--walky-border);
  border-radius: 8px;
  background: var(--walky-surface-raised);
  color: var(--walky-ink);
  box-shadow: var(--walky-shadow);
  cursor: pointer;
  z-index: 2;

  svg {
    width: 21px;
    height: 21px;
    display: block;
  }

  &:hover,
  &:focus-visible {
    color: #fff;
    background: var(--walky-orange);
    border-color: var(--walky-orange);
    outline: none;
  }
`;

const LockedPreview = styled.div`
  display: grid;
  gap: 14px;
  align-content: center;
  min-height: 260px;
  text-align: center;

  h2 {
    margin: 0;
    color: var(--walky-ink);
    font-size: clamp(28px, 4vw, 42px);
    line-height: 1;
  }

  p {
    width: min(520px, 100%);
    margin: 0 auto;
    color: var(--walky-muted);
    font-size: 16px;
    font-weight: 800;
    line-height: 1.45;
  }

  button {
    justify-self: center;
    min-height: 46px;
    padding: 0 22px;
    border: 1px solid rgba(244, 90, 10, 0.5);
    border-radius: 8px;
    background: linear-gradient(135deg, var(--walky-orange), #ff7a1a);
    color: #fff;
    cursor: pointer;
    font-size: 15px;
    font-weight: 900;
    text-transform: uppercase;
    box-shadow: 0 14px 28px rgba(244, 90, 10, 0.2);
  }

  button:hover,
  button:focus-visible {
    filter: saturate(1.06) brightness(1.02);
    outline: none;
  }
`;
